/**
 * Resolves a currency field (which may be a string code or a relation object)
 * into a plain currency code like "USD" or "EGP".
 * Optionally accepts a currencies list to look up code by documentId.
 */
export const resolveCurrencyCode = (currencyField: any, currenciesList?: any[] | null): string => {
  if (!currencyField) return 'EGP';
  if (typeof currencyField === 'string') {
    if (currenciesList?.length) {
      const matched = currenciesList.find(
        (c: any) => c.documentId === currencyField || c.code === currencyField
      );
      if (matched?.code) return matched.code;
    }
    return currencyField;
  }
  if (typeof currencyField === 'object') {
    if (currencyField.code) return currencyField.code;
    if (currencyField.name) return currencyField.name;
    // If only documentId/id, look up from currencies list
    if (currenciesList?.length && (currencyField.documentId || currencyField.id)) {
      const matched = currenciesList.find(
        (c: any) => c.documentId === currencyField.documentId || c.id === currencyField.id
      );
      if (matched?.code) return matched.code;
    }
    return 'EGP';
  }
  return 'EGP';
};

/**
 * Resolves a currency field (relation object, documentId string, legacy code
 * string like "USD", or numeric id) into the matching currency documentId,
 * suitable for pre-selecting a dropdown whose option values are documentIds.
 * Returns '' when no match is found.
 */
export const resolveCurrencyDocumentId = (
  currencyField: any,
  currenciesList?: any[] | null
): string => {
  if (!currencyField) return '';
  if (typeof currencyField === 'object') {
    if (currencyField.documentId) return currencyField.documentId;
    if (currenciesList?.length) {
      const matched = currenciesList.find(
        (c: any) =>
          (currencyField.id && String(c.id) === String(currencyField.id)) ||
          (currencyField.code && c.code === currencyField.code)
      );
      if (matched) return matched.documentId || String(matched.id);
    }
    return currencyField.id ? String(currencyField.id) : '';
  }
  const value = String(currencyField);
  if (currenciesList?.length) {
    const matched = currenciesList.find(
      (c: any) => c.documentId === value || c.code === value || String(c.id) === value
    );
    if (matched) return matched.documentId || String(matched.id);
  }
  return value;
};

/**
 * Gets the exchange rate to EGP for a given currency field.
 * Looks up the rate from the currency object itself, or from the currencies list.
 * Falls back to the provided defaultRate.
 */
export const getCurrencyRate = (
  currencyField: any,
  currenciesList: any[] | null | undefined,
  defaultRate: number = 1
): number => {
  if (!currencyField) return 1;

  if (typeof currencyField === 'object') {
    if (currencyField.exchangeRateToEGP && currencyField.exchangeRateToEGP > 1) {
      return currencyField.exchangeRateToEGP;
    }
    if (currenciesList?.length && (currencyField.documentId || currencyField.id)) {
      const matched = currenciesList.find(
        (c: any) =>
          (currencyField.documentId && c.documentId === currencyField.documentId) ||
          (currencyField.id && (c.id === currencyField.id || String(c.id) === String(currencyField.id)))
      );
      if (matched?.exchangeRateToEGP) return matched.exchangeRateToEGP;
    }
    // Try resolving by code first, then look up rate
    const code = currencyField.code || currencyField.name;
    if (code && currenciesList?.length) {
      const matched = currenciesList.find((c: any) => c.code === code);
      if (matched?.exchangeRateToEGP) return matched.exchangeRateToEGP;
    }
  }

  if (typeof currencyField === 'string' && currenciesList?.length) {
    const matched = currenciesList.find(
      (c: any) => c.code === currencyField || c.documentId === currencyField || String(c.id) === currencyField
    );
    if (matched?.exchangeRateToEGP) return matched.exchangeRateToEGP;
  }

  return defaultRate;
};

/**
 * Checks if a currency is non-EGP (i.e. foreign currency like USD).
 */
export const isForeignCurrency = (currencyField: any, currenciesList?: any[] | null): boolean => {
  const code = resolveCurrencyCode(currencyField, currenciesList);
  return code !== 'EGP';
};

/**
 * Resolves the effective exchange rate for a record.
 * If the record has a meaningful exchangeRate (> 1 for foreign currencies), use it.
 * Otherwise, look up from the currency relation or currencies list.
 */
export const resolveEffectiveRate = (
  recordExchangeRate: number | undefined | null,
  currencyField: any,
  currenciesList: any[] | null | undefined,
  defaultRate: number = 50
): number => {
  const code = resolveCurrencyCode(currencyField, currenciesList);
  const isForeign = code !== 'EGP';

  if (!isForeign) return 1;

  if (recordExchangeRate && recordExchangeRate > 1) {
    return recordExchangeRate;
  }

  const lookupRate = getCurrencyRate(currencyField, currenciesList, defaultRate);
  return lookupRate > 1 ? lookupRate : defaultRate;
};

/**
 * Converts an amount to both USD and EGP values.
 * @param amount - The original amount
 * @param currencyField - The currency (string or relation object)
 * @param rate - The exchange rate (currency to EGP)
 */
export const convertAmounts = (
  amount: number,
  currencyField: any,
  rate: number
): { usd: number; egp: number } => {
  const isUSD = isForeignCurrency(currencyField);
  return {
    usd: isUSD ? amount : (rate > 0 ? amount / rate : 0),
    egp: isUSD ? amount * rate : amount
  };
};
